import { useMutation, useQuery } from "@tanstack/react-query";
import jwtDecode from "jwt-decode";
import { get, post, put } from "./request";
import { Storage } from "./storage";
import { API_DELIMITER } from "../utils/constants";
import { USER_PRIVILEGES } from "../components/pages/UserManagement/Roles/RoleConfiguration";

const AUTH_TOKEN = 'auth-token';
const USER_PRIVILEGES_KEY = 'user-privileges';
const USER_ROLE = 'user-role';

export function getAuthToken() {
    return Storage.getValue(AUTH_TOKEN);
}

export function getUserPrivileges() {
    const userPrivileges = Storage.getValue(USER_PRIVILEGES_KEY) || '';
    return userPrivileges.split(API_DELIMITER);
}

export function setUserRole(roles: any) {
  const role = roles[0];
  Storage.setValue(USER_ROLE, role.name);
  Storage.setValue(USER_PRIVILEGES_KEY, role.pages);
}

export function getUserRole() {
    return Storage.getValue(USER_ROLE) || '';
}

export function setAuthToken(token: string) {
    Storage.setValue(AUTH_TOKEN, token);
}

export function clearAuthToken() {
    Storage.removeValue([AUTH_TOKEN]);
}

export function setUserSession(token: string) {
  const parsedToken = parseToken(token);
  Storage.setValue(AUTH_TOKEN, token);
  if (parsedToken && parsedToken.roles) {
    setUserRole(parsedToken.roles);
  }
}

export function clearUserSession() {
    Storage.removeValue([AUTH_TOKEN, USER_PRIVILEGES_KEY, USER_ROLE]);
}

export function initializeTabSession() {
    const token = getAuthToken();
    const currentRole = getUserRole();

    if (token && !currentRole) {
        const parsedToken = parseToken(token);
        if (parsedToken && parsedToken.roles && parsedToken.roles.length > 0) {
            setUserRole(parsedToken.roles);
        }
    }
}

export function getUserDetails(_token = ''): any {
    const token = getAuthToken() || _token;
    const parsedToken = parseToken(token);
    return parsedToken;
}

export function parseToken(token: string): any {
    if (token) {
        try {
            return jwtDecode(token);
        } catch (e) {
            return null;
        }
    }
    return null;
}

export function useUserLogin(onSuccess?: any, onError?: any) {
    const { mutate: userLogin, error, isLoading } = useMutation(
        ['userLogin'],
        async ({ username, password, recaptchaToken }: any) => await post(`/users/sign_in`, {
          username: username,
          password: password,
          recaptcha_token: recaptchaToken
        }, null, false),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { userLogin, error, isLoading };
}

export function useValidateToken(token: string) {
    const { data, isFetching } = useQuery(
        ['validateToken', token],
        async () => await get(`/api/v1/auth/IsValidToken?token=${token}`),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!token
        }
    );
    return { status: (data || {}).data || {}, isFetching };
}

export function useValidateUrl(token: string) {
    const { data, isFetching } = useQuery(
        ['validateUrl', token],
        async () => await get(`/users/password/edit?reset_password_token=${token}`),
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: !!token
        }
    );
    return { status: data, isFetching };
}

export function useChangePassword(onSuccess?: any, onError?: any) {
    const { mutate: changePassword, error } = useMutation(
        ['changePassword'],
        async ({ newPassword, token }: any) => {
            const url = `/users/password`;
            const _response = await put(url, { user: { password: newPassword, password_confirmation: newPassword, reset_password_token: token } });
            return _response
        },
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { changePassword, error };
}

export function useForgotPassword(onSuccess?: any, onError?: any) {
    const { mutate: forgotPassword, error } = useMutation(
        ['forgotPassword'],
        async ({ username }: any) => await post(`/users/password`, { user: { username: username } }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { forgotPassword, error };
}

export function useGenerateOTP(onSuccess?: any, onError?: any) {
    const { mutate: generateOTP, error, isLoading } = useMutation(
        ['generateOTP'],
        async ({ username }: any) => await get(`/api/v1/auth/GenerateOTP?username=${username}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { generateOTP, error, isLoading };
}

export function useLoginWithOtp(onSuccess?: any, onError?: any) {
    const { mutate: loginWithOtp, error, isLoading } = useMutation(
        ['updateLocation'],
        async ({ username, otp, token }: any) => await post(`/api/v1/auth/LoginWithOtp?username=${username}&loginOtp=${otp}`, {}, { Authorization: token }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { loginWithOtp, error, isLoading };
}

export function useVerifyOTP(onSuccess?: any, onError?: any) {
    const { mutate: verifyOTP, error, isLoading } = useMutation(
        ['verifyOTP'],
        async ({ username, otp }: any) => await post(`/users/verify_otp`, { username, otp: otp }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { verifyOTP, error, isLoading };
}

export function useResendOTP(onSuccess?: any, onError?: any) {
    const { mutate: resendOTP, error, isLoading } = useMutation(
        ['resendOTP'],
        async ({username}: any) => await post(`/users/resend_otp`, {username}),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { resendOTP, error, isLoading };
}

export function useUnlockAccount(onSuccess?: any, onError?: any) {
    const { mutate: unlockAccount, error, isLoading } = useMutation(
        ['unlockAccount'],
        async ({ unlock_token }: any) => await get(`/users/unlock?unlock_token=${unlock_token}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess(response)
            }
        }
    );
    return { unlockAccount, error, isLoading };
}

export function hasUserAccess(key: string) {
    const privileges = (Storage.getValue(USER_PRIVILEGES_KEY) || '').split(API_DELIMITER);
    return privileges.includes(key);
}

export function isAdmin() {
    return !hasUserAccess(USER_PRIVILEGES.SUBMITTER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.REVIEWER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD)
        && !hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)
}

export function isComplianceUser() {
    return hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)
        || hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);
}

export function useSSOCheck(onSuccess?: any, onError?: any) {
    const { mutate: checkSSO, error, isLoading } = useMutation(
        ['ssoCheck'],
        async ({ code }: any) => await get(`/api/v1/companies/sso_check?code=${code}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { checkSSO, error, isLoading };
}

export function useSSOInit(onSuccess?: any, onError?: any) {
    const { mutate: initSSO, error, isLoading } = useMutation(
        ['ssoInit'],
        async ({ code, redirectUrl }: any) => await post(`/api/v1/auth/saml/init`, {
            code: code,
            redirect_url: redirectUrl
        }),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { initSSO, error, isLoading };
}

export function useCompanyLookup(onSuccess?: any, onError?: any) {
    const { mutate: lookupCompany, error, isLoading } = useMutation(
        ['companyLookup'],
        async ({ code }: any) => await get(`/api/v1/companies/sso_check?code=${code}`),
        {
            onError,
            onSuccess: (response) => {
                onSuccess((response || {}).data || {})
            }
        }
    );
    return { lookupCompany, error, isLoading };
}

export function useLogout(onSuccess?: any, onError?: any) {
    const { mutate: logout, error, isLoading } = useMutation(
        ['logout'],
        async () => await post(`/api/v1/auth/logout`, {}),
        {
            onError,
            onSuccess: (response) => {
                clearUserSession();
                onSuccess && onSuccess(response);
            }
        }
    );
    return { logout, error, isLoading };
}

export function useLogoutAll(onSuccess?: any, onError?: any) {
    const { mutate: logoutAll, error, isLoading } = useMutation(
        ['logoutAll'],
        async () => await post(`/api/v1/auth/logout_all`, {}),
        {
            onError,
            onSuccess: (response) => {
                clearUserSession();
                onSuccess && onSuccess(response);
            }
        }
    );
    return { logoutAll, error, isLoading };
}
